import { useEffect, useState, useRef } from "react";
import { useReducedMotion } from "framer-motion";

interface TypingEffectProps {
  segments: { text: string; className?: string }[];
  typingSpeed?: number;
  onComplete?: () => void;
  className?: string;
}

const TypingEffect = ({ segments, typingSpeed = 45, onComplete, className = "" }: TypingEffectProps) => {
  const prefersReducedMotion = useReducedMotion();
  const [displayedChars, setDisplayedChars] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [showCursor, setShowCursor] = useState(true);
  const totalChars = segments.reduce((sum, seg) => sum + seg.text.length, 0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const cursorIntervalRef = useRef<ReturnType<typeof setInterval>>();

  // Skip animation for reduced motion
  useEffect(() => {
    if (prefersReducedMotion) {
      setDisplayedChars(totalChars);
      setIsComplete(true);
      setShowCursor(false);
      onComplete?.();
    }
  }, [prefersReducedMotion, totalChars, onComplete]);

  // Typing animation
  useEffect(() => {
    if (prefersReducedMotion || isComplete) return;

    if (displayedChars >= totalChars) {
      setIsComplete(true);
      // Keep cursor blinking briefly, then hide
      setTimeout(() => {
        setShowCursor(false);
        onComplete?.();
      }, 1200);
      return;
    }

    // Find which character we're on to apply realistic timing
    let charIndex = displayedChars;
    let currentChar = "";
    let charsLeft = charIndex;
    for (const seg of segments) {
      if (charsLeft < seg.text.length) {
        currentChar = seg.text[charsLeft];
        break;
      }
      charsLeft -= seg.text.length;
    }

    // Natural pauses: longer after punctuation, shorter for spaces
    let delay = typingSpeed;
    if (currentChar === "." || currentChar === "," || currentChar === "—") {
      delay = typingSpeed * 4;
    } else if (currentChar === " ") {
      delay = typingSpeed * 0.6;
    } else {
      // Slight randomness for natural feel
      delay = typingSpeed + (Math.random() - 0.5) * typingSpeed * 0.5;
    }

    timeoutRef.current = setTimeout(() => {
      setDisplayedChars((prev) => prev + 1);
    }, delay);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [displayedChars, totalChars, typingSpeed, segments, isComplete, prefersReducedMotion, onComplete]);

  // Cursor blink
  useEffect(() => {
    if (prefersReducedMotion) return;
    cursorIntervalRef.current = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530);
    return () => {
      if (cursorIntervalRef.current) clearInterval(cursorIntervalRef.current);
    };
  }, [prefersReducedMotion]);

  // Build the visible text from segments
  const renderSegments = () => {
    let charsRendered = 0;
    return segments.map((segment, i) => {
      const segStart = charsRendered;
      const segEnd = segStart + segment.text.length;
      charsRendered = segEnd;

      if (displayedChars <= segStart) return null;

      const visibleLength = Math.min(displayedChars - segStart, segment.text.length);
      const visibleText = segment.text.slice(0, visibleLength);

      return (
        <span key={i} className={segment.className}>
          {visibleText}
        </span>
      );
    });
  };

  return (
    <span className={className}>
      {renderSegments()}
      {!isComplete && (
        <span
          className={`inline-block w-[3px] h-[0.65em] bg-sage align-middle ml-0.5 rounded-full transition-opacity duration-100 ${
            showCursor ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
    </span>
  );
};

export default TypingEffect;
